import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { DatePicker } from '../../../../lib/components';
import { useTranslation } from 'react-i18next';
import { MonthPicker } from '../../../../lib/components/atomic/input/monthPicker';
import { useFormikContext, FormikValues } from 'formik';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { TooltipComponent } from '../../../../lib/components/atomic/feedback';

interface MonthRangeProps {
  hideInterval?: boolean;
  maxDate?: Date;
  handleStartDateChange: (date: string) => void;
  handleEndDateChange: (date: string | null) => void;
}

export const MonthRange = ({
  hideInterval = false,
  maxDate,
  handleStartDateChange,
  handleEndDateChange
}: MonthRangeProps) => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const { values, touched, errors, setFieldValue } = useFormikContext<FormikValues>();

  const disableEditing = newBonus.editingNotAllowed;

  const endDateDisabled =
    ['BIRTHDAY', 'ANNIVERSARY'].includes(values.specialOccasionType) &&
    values.frequency === 'ONE_TIME';

  const endDateHidden = endDateDisabled && disableEditing;

  return (
    <div className="flex gap-x-4 w-full">
      <TooltipComponent title={disableEditing ? t('bonus:tooltip:editingNotAllowed') : ''} arrow>
        <div className="w-full">
          <InputFieldLabel
            label={t('form:START_DATE')}
            required
            tooltip={
              disableEditing ? t('bonus:tooltip:editingNotAllowed') : t('bonus:tooltip:startDate')
            }
            disabled={disableEditing}
          />
          <DatePicker
            variant="month"
            initialValue={values.startAccountingPeriod?.toString()}
            placeholder={t('form:START_DATE')}
            required
            error={touched.startAccountingPeriod && !!errors.startAccountingPeriod}
            helperText={(touched.startAccountingPeriod && errors.startAccountingPeriod) as string}
            onChange={(date) => handleStartDateChange(date)}
            disabled={disableEditing}
            minDate={new Date(new Date().getFullYear(), 0, 1)}
            maxDate={maxDate}
          />
        </div>
      </TooltipComponent>
      {!hideInterval && (
        <div className="w-full">
          <InputFieldLabel
            label={t('bonus:interval')}
            required
            tooltip={t('bonus:tooltip:interval')}
            disabled={disableEditing}
          />
          <MonthPicker
            initialValue={values.interval as number[]}
            placeholder={t('bonus:payoutInterval')}
            required
            error={touched.interval && !!errors.interval}
            helperText={(touched.interval && errors.interval) as string}
            onChange={(months) => {
              setFieldValue('interval', months);
            }}
            disabled={disableEditing}
          />
        </div>
      )}
      {!endDateHidden && (
        <div className="w-full">
          <InputFieldLabel
            label={t('form:END_DATE') + ' ' + t('common:OPTIONAL')}
            tooltip={t('bonus:tooltip:endDate')}
            disabled={endDateDisabled}
          />
          <DatePicker
            variant="month"
            initialValue={values.endAccountingPeriod?.toString()}
            placeholder={t('form:END_DATE')}
            error={touched.endAccountingPeriod && !!errors.endAccountingPeriod}
            helperText={(touched.endAccountingPeriod && errors.endAccountingPeriod) as string}
            onChange={(date) => handleEndDateChange(date)}
            onClear={() => handleEndDateChange(null)}
            disabled={endDateDisabled}
            minDate={new Date(new Date().getFullYear(), 0, 1)}
            maxDate={maxDate}
          />
        </div>
      )}
    </div>
  );
};
